// pages/InvoiceEntry.tsx

import { useEffect, useRef } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertCircle } from 'lucide-react';
import { useInvoiceEntryStore } from '@/stores/invoiceEntryStore';
import {
  StepIndicator,
  UploadStep,
  ExtractionStep,
  BatchPreviewStep,
  DuplicateStep,
} from './purchase-entry';

const API_URL = import.meta.env.VITE_API_URL || '';

export default function InvoiceEntry() {
  const {
    currentStep,
    transactionType,
    setTransactionType,
    error,
    setCompany,
    setError,
    reset,
    files,
    invoices,
  } = useInvoiceEntryStore();
  const fetchedRef = useRef(false);

  // Fetch company from tenant context on mount (only once)
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    let isMounted = true;

    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/invoice-entry/company`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch company');
        }

        const data = await response.json();
        if (!isMounted) return;

        const companyName = typeof data.company === 'object'
          ? data.company.name
          : data.company;
        setCompany(companyName);
      } catch (err) {
        if (!isMounted) return;
        console.error('Failed to fetch company:', err);
        setError('Failed to connect to Tally. Please ensure Tally Connector is running.');
      }
    };

    fetchCompany();

    return () => {
      isMounted = false;
    };
  }, [setCompany, setError]);

  // Reset store on unmount
  useEffect(() => {
    return () => {
      fetchedRef.current = false;
      reset();
    };
  }, [reset]);

  // Handle tab change with confirmation if data exists
  const handleTabChange = (value: 'purchase' | 'sales') => {
    const hasData = currentStep !== 'upload' || files.length > 0 || invoices.length > 0;

    if (hasData) {
      const confirmed = window.confirm(
        'Switching tabs will clear your current progress. Continue?'
      );
      if (confirmed) {
        reset();
        setTransactionType(value);
      }
    } else {
      reset();
      setTransactionType(value);
    }
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 'upload':
        return <UploadStep />;
      case 'extraction':
        return <ExtractionStep />;
      case 'batch-preview':
        return <BatchPreviewStep />;
      case 'duplicate':
        return <DuplicateStep />;
      default:
        return <UploadStep />;
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 max-w-5xl">
        <Tabs value={transactionType} onValueChange={handleTabChange as (value: string) => void}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="purchase">Purchase Invoices</TabsTrigger>
            <TabsTrigger value="sales">Sales Invoices</TabsTrigger>
          </TabsList>

          <TabsContent value={transactionType}>
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">
                  {transactionType === 'purchase' ? 'Purchase' : 'Sales'} Invoice Entry
                </CardTitle>
                <CardDescription>
                  Upload {transactionType} invoices to extract data and create vouchers in Tally
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <StepIndicator />

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {renderStep()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
